"use client";

import { useState } from "react";
import Image from "next/image";
import { Mail, MapPin } from "lucide-react";
import { cn } from "@/lib/cn";

export type TeamMemberView = {
  id: string;
  name: string;
  role: string; // already localized
  photo: string;
  location?: string;
  email?: string;
};

/**
 * Client team grid with a Standort (location) filter dropdown. Roles are
 * pre-localized on the server. The dropdown only appears when members span
 * more than one location.
 */
export function TeamDirectory({
  members,
  cols = 3,
  allLabel,
  filterLabel,
}: {
  members: TeamMemberView[];
  cols?: 2 | 3 | 4;
  allLabel: string;
  filterLabel: string;
}) {
  const locations = Array.from(
    new Set(members.map((m) => m.location).filter(Boolean) as string[]),
  ).sort((a, b) => a.localeCompare(b, "de"));
  const [loc, setLoc] = useState("");
  const visible = loc ? members.filter((m) => m.location === loc) : members;

  return (
    <div>
      {locations.length > 1 ? (
        <div className="mt-10 flex flex-wrap items-center gap-2.5">
          <label htmlFor="team-loc" className="inline-flex items-center gap-1.5 text-sm text-ink-600">
            <MapPin className="h-4 w-4 text-brand-600" aria-hidden />
            {filterLabel}
          </label>
          <select
            id="team-loc"
            value={loc}
            onChange={(e) => setLoc(e.target.value)}
            className="input h-10 w-auto min-w-[12rem] py-0"
          >
            <option value="">{allLabel}</option>
            {locations.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
          <span className="text-xs text-ink-400 tabnums">
            {visible.length}/{members.length}
          </span>
        </div>
      ) : null}

      <ul
        className={cn(
          "mt-8 grid gap-6 sm:grid-cols-2",
          cols === 3 && "lg:grid-cols-3",
          cols === 4 && "lg:grid-cols-4",
        )}
      >
        {visible.map((m) => (
          <li
            key={m.id}
            className="rounded-xl border border-line-200 bg-canvas p-5 transition-colors hover:border-ink-700/30"
          >
            <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-soft">
              <Image
                src={m.photo}
                alt={m.name}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 90vw, 320px"
              />
            </div>
            <h3 className="mt-5 font-display text-base font-semibold text-ink-900">{m.name}</h3>
            <p className="mt-1 text-sm leading-snug text-ink-700">{m.role}</p>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-ink-500">
              {m.location ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" aria-hidden />
                  {m.location}
                </span>
              ) : null}
              {m.email ? (
                <a
                  href={`mailto:${m.email}`}
                  className="inline-flex items-center gap-1 hover:text-brand-600"
                >
                  <Mail className="h-3 w-3" aria-hidden />
                  {m.email}
                </a>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
